import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';

/**
 * Editing panels in the admin preview may spill past the preview's edges.
 * The preview is a same-origin iframe, so they are drawn in the admin page
 * instead: in a shadow root (the site's styles copied in, the admin's kept
 * out) laid over the iframe at its zoom. Inside the layer, `position: fixed`
 * uses the preview's own coordinates, so panels place themselves as before.
 */

let shared = null;

function frameElement() {
    if (typeof window === 'undefined' || window.parent === window) return null;
    try {
        return window.frameElement || null;
    } catch {
        return null;
    }
}

function mount(frame) {
    const doc = frame.ownerDocument;
    const host = doc.createElement('div');
    host.setAttribute('data-preview-overlay', '');
    host.style.cssText = 'position:fixed;left:0;top:0;width:0;height:0;z-index:2147483640;';
    doc.body.appendChild(host);
    const root = host.attachShadow({ mode: 'open' });
    const styles = doc.createElement('div');
    const layer = doc.createElement('div');
    root.append(styles, layer);
    const copyStyles = () => {
        styles.replaceChildren(...[...document.querySelectorAll('link[rel="stylesheet"], style')]
            .map((node) => doc.importNode(node, true)));
    };
    copyStyles();
    const observer = new MutationObserver(copyStyles);
    observer.observe(document.head, { childList: true, subtree: true, characterData: true });
    window.addEventListener('pagehide', () => {
        observer.disconnect();
        host.remove();
        shared = null;
    }, { once: true });
    return { frame, doc, host, root, layer };
}

/** The admin-page layer over this preview, or null outside the admin preview. */
export function frameHost() {
    const frame = frameElement();
    if (!frame) return null;
    if (!shared || shared.frame !== frame || !shared.host.isConnected) shared = mount(frame);
    return shared;
}

/** Where the preview sits in the admin page: its offset and zoom. */
export function frameGeometry(host) {
    const rect = host.frame.getBoundingClientRect();
    const scale = host.frame.clientWidth ? rect.width / host.frame.clientWidth : 1;
    const view = host.doc.defaultView;
    return {
        left: rect.left + host.frame.clientLeft * scale,
        top: rect.top + host.frame.clientTop * scale,
        scale: scale || 1,
        // The admin window, in the preview's coordinates.
        bounds: {
            left: -(rect.left / scale),
            top: -(rect.top / scale),
            right: (view.innerWidth - rect.left) / scale,
            bottom: (view.innerHeight - rect.top) / scale,
        },
    };
}

/** A rect measured in the admin page (a control in the layer) in the preview's coordinates. */
export function rectInFrame(host, rect) {
    const { left, top, scale } = frameGeometry(host);
    return {
        left: (rect.left - left) / scale,
        top: (rect.top - top) / scale,
        right: (rect.right - left) / scale,
        bottom: (rect.bottom - top) / scale,
        width: rect.width / scale,
        height: rect.height / scale,
    };
}

/** The admin-page layer, once mounted in the browser. */
export function useFrameHost() {
    const [host, setHost] = useState(null);
    useEffect(() => setHost(frameHost()), []);
    return host;
}

function useGeometry(host) {
    const [geometry, setGeometry] = useState(() => (host ? frameGeometry(host) : null));
    useEffect(() => {
        if (!host) return undefined;
        const update = () => setGeometry((prev) => {
            const next = frameGeometry(host);
            return prev && prev.left === next.left && prev.top === next.top && prev.scale === next.scale
                && prev.bounds.right === next.bounds.right && prev.bounds.bottom === next.bounds.bottom
                ? prev
                : next;
        });
        update();
        const view = host.doc.defaultView;
        view.addEventListener('resize', update);
        view.addEventListener('scroll', update, true);
        const observer = new view.ResizeObserver(update);
        observer.observe(host.frame);
        const timer = setInterval(update, 500);
        return () => {
            view.removeEventListener('resize', update);
            view.removeEventListener('scroll', update, true);
            observer.disconnect();
            clearInterval(timer);
        };
    }, [host]);
    return geometry;
}

/**
 * Children drawn over the preview in the admin page, free to spill past its
 * edges; drawn in place when there is no admin page around the preview.
 */
export default function OutsideFrame({ children, host: given }) {
    const own = useFrameHost();
    const host = given === undefined ? own : given;
    const geometry = useGeometry(host);
    if (!host || !geometry) return children;
    return createPortal(
        <div
            data-outside-frame
            style={{
                position: 'fixed',
                left: geometry.left,
                top: geometry.top,
                width: window.innerWidth,
                height: window.innerHeight,
                transform: `scale(${geometry.scale})`,
                transformOrigin: '0 0',
                pointerEvents: 'none',
            }}
        >
            <div style={{ pointerEvents: 'auto' }}>{children}</div>
        </div>,
        host.layer,
    );
}

OutsideFrame.propTypes = {
    children: PropTypes.node,
    /** The layer to draw in; omit to find it. null draws in place. */
    host: PropTypes.object,
};
